import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import MgmtLayout from '@/components/feature/MgmtLayout';
import { mockProjects } from '@/mocks/projects';
import { mockPhotos } from '@/mocks/updates';
import PhotoLightbox, { type LightboxPhoto } from '@/components/feature/PhotoLightbox';

const tagColors: Record<string, string> = {
  Before: '#1D4ED8',
  Progress: '#E8621A',
  After: '#059669',
  Issue: '#EF4444',
};

export default function StageProgress() {
  const { id, stageId } = useParams<{ id: string; stageId: string }>();
  const navigate = useNavigate();
  const [showUpdateForm, setShowUpdateForm] = useState(false);
  const [updateText, setUpdateText] = useState('');
  const [selectedTag, setSelectedTag] = useState<'Progress' | 'Issue' | 'Complete'>('Progress');
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);

  const { t } = useTranslation();
  const project = mockProjects.find((p) => p.id === id) || mockProjects[0];
  const stage = project.stages.find((s) => s.id === stageId) || project.stages[3];
  const stagePhotos = mockPhotos.slice(0, stage.photoCount > 0 ? Math.min(stage.photoCount, mockPhotos.length) : 4);

  const stageStatusStyle: Record<string, { bg: string; text: string; label: string }> = {
    completed: { bg: '#F0FDF4', text: '#059669', label: t('stages.statusCompleted') },
    active: { bg: '#FEF0E7', text: '#E8621A', label: t('stages.statusInProgress') },
    upcoming: { bg: '#F9FAFB', text: '#9CA3AF', label: t('payments.statusUpcoming') },
    delayed: { bg: '#FEF2F2', text: '#EF4444', label: t('stages.statusDelayed') },
  };

  const style = stageStatusStyle[stage.status];

  const lightboxPhotos: LightboxPhoto[] = stagePhotos.map((p) => ({
    url: p.url,
    caption: p.caption,
    tag: p.tag,
    tagColor: tagColors[p.tag],
    uploadedBy: p.uploadedBy,
    date: p.uploadedAt,
  }));

  return (
    <MgmtLayout showBack backTo={`/management/projects/${id}`} title={stage.name}>
      {lightboxIndex !== null && (
        <PhotoLightbox
          photos={lightboxPhotos}
          initialIndex={lightboxIndex}
          onClose={() => setLightboxIndex(null)}
        />
      )}
      <div className="px-4 pt-4 pb-6">
        {/* Stage header card */}
        <div className="bg-[#1A1A1A] rounded-2xl p-5 mb-4 relative overflow-hidden">
          <div className="absolute right-0 top-0 bottom-0 w-32 opacity-10">
            <div className="w-full h-full bg-[#E8621A] rounded-l-full"></div>
          </div>
          <div className="relative">
            <div className="flex items-center gap-2 mb-3">
              <span
                className="text-xs font-bold px-2.5 py-1 rounded-full"
                style={{ backgroundColor: `${style.text}20`, color: style.text }}
              >
                {style.label}
              </span>
              <span className="text-[#6B7280] text-xs">{stage.startDate} — {stage.endDate}</span>
            </div>
            <h2 className="text-white text-xl font-bold mb-1">{stage.name}</h2>
            <p className="text-[#9CA3AF] text-sm leading-relaxed">{stage.description}</p>

            {stage.status === 'active' && (
              <div className="mt-4">
                <div className="flex items-center justify-between mb-1.5">
                  <span className="text-[#9CA3AF] text-xs">Stage progress</span>
                  <span className="text-[#E8621A] text-sm font-bold">{stage.progress}%</span>
                </div>
                <div className="h-2 bg-[#374151] rounded-full overflow-hidden">
                  <div
                    className="h-full rounded-full bg-[#E8621A] transition-all"
                    style={{ width: `${stage.progress}%` }}
                  ></div>
                </div>
              </div>
            )}

            <div className="flex items-center gap-4 mt-4">
              <div className="flex items-center gap-1.5">
                <i className="ri-image-line text-[#9CA3AF] text-sm"></i>
                <span className="text-[#9CA3AF] text-xs">{stage.photoCount} {t('stageProgress.photos')}</span>
              </div>
              <div className="flex items-center gap-1.5">
                <i className="ri-file-list-3-line text-[#9CA3AF] text-sm"></i>
                <span className="text-[#9CA3AF] text-xs">{stage.updateCount} {t('stageProgress.updates')}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Photo grid */}
        {stagePhotos.length > 0 && (
          <div className="mb-4">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-bold text-[#1A1A1A] text-sm">{t('stageProgress.stagePhotos')}</h3>
              <button className="flex items-center gap-1.5 text-[#E8621A] text-sm font-medium cursor-pointer whitespace-nowrap">
                <i className="ri-upload-2-line text-base"></i>
                {t('stageProgress.upload')}
              </button>
            </div>
            {/* Featured photo */}
            <button
              onClick={() => setLightboxIndex(0)}
              className="w-full h-48 rounded-2xl overflow-hidden mb-2 relative group cursor-pointer text-left"
            >
              <img
                src={stagePhotos[0].url}
                alt={stagePhotos[0].caption}
                className="w-full h-full object-cover object-top"
              />
              <div className="absolute inset-0 bg-black/0 group-active:bg-black/20 transition-colors"></div>
              <div
                className="absolute top-3 left-3 text-[10px] font-bold px-2 py-0.5 rounded-full text-white"
                style={{ backgroundColor: tagColors[stagePhotos[0].tag] }}
              >
                {stagePhotos[0].tag}
              </div>
              <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                <div className="w-10 h-10 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center">
                  <i className="ri-zoom-in-line text-white text-xl"></i>
                </div>
              </div>
              <div className="absolute bottom-3 left-3 right-3">
                <p className="text-white text-sm font-semibold">{stagePhotos[0].caption}</p>
                <p className="text-white/60 text-xs">{stagePhotos[0].uploadedBy} · {stagePhotos[0].uploadedAt}</p>
              </div>
            </button>
            {/* Thumbnail row */}
            {stagePhotos.length > 1 && (
              <div className="grid grid-cols-3 gap-2">
                {stagePhotos.slice(1, 4).map((photo, i) => (
                  <button key={photo.id} onClick={() => setLightboxIndex(i + 1)} className="relative rounded-xl overflow-hidden aspect-[4/3] cursor-pointer group text-left">
                    <img src={photo.url} alt={photo.caption} className="w-full h-full object-cover object-top" />
                    <div className="absolute inset-0 bg-black/0 group-active:bg-black/20 transition-colors"></div>
                    <div
                      className="absolute top-1.5 left-1.5 text-[9px] font-bold px-1.5 py-0.5 rounded-full text-white"
                      style={{ backgroundColor: tagColors[photo.tag] }}
                    >
                      {photo.tag}
                    </div>
                    {i === 2 && stagePhotos.length > 4 ? (
                      <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                        <span className="text-white font-bold text-sm">{t('stageProgress.morePhotos', { n: stagePhotos.length - 4 })}</span>
                      </div>
                    ) : (
                      <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                        <div className="w-8 h-8 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center">
                          <i className="ri-zoom-in-line text-white text-base"></i>
                        </div>
                      </div>
                    )}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Updates feed */}
        <div className="mb-4">
          <h3 className="font-bold text-[#1A1A1A] text-sm mb-3">{t('stageProgress.stageUpdates')}</h3>
          <div className="space-y-3">
            {[
              { role: 'Subcontractor', roleLabel: t('stageProgress.subcontractorReport'), icon: 'ri-tools-line', color: '#059669', type: 'Progress', text: 'Kitchen joinery installed and benchtop templating complete. Tiler on site from Wednesday.', time: '18 min ago' },
              { role: 'Subcontractor', roleLabel: t('stageProgress.subcontractorReport'), icon: 'ri-tools-line', color: '#059669', type: 'Issue', text: 'Tile delivery delayed 3 days — Calacatta floor tiles on backorder. No impact to PC date.', time: '3 hrs ago' },
              { role: 'Management', roleLabel: t('stageProgress.managementUpdate'), icon: 'ri-building-2-line', color: '#E8621A', type: 'Progress', text: 'Weatherboard replacement on north elevation 80% complete. Finishing tomorrow.', time: '1 day ago' },
            ].map((u, i) => (
              <div key={i} className="bg-white rounded-2xl border border-gray-100 p-4">
                <div className="flex items-start gap-3">
                  <div
                    className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
                    style={{ backgroundColor: `${u.color}15` }}
                  >
                    <i className={`${u.icon} text-sm`} style={{ color: u.color }}></i>
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-sm font-semibold" style={{ color: u.color }}>{u.roleLabel}</span>
                      <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-[#FEF0E7] text-[#E8621A]">{u.type}</span>
                    </div>
                    <p className="text-[#6B7280] text-sm leading-relaxed">{u.text}</p>
                    <p className="text-[#9CA3AF] text-xs mt-1.5">{u.time}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Post update form */}
        {showUpdateForm && (
          <div className="bg-white rounded-2xl border border-gray-100 p-4 mb-4">
            <h4 className="font-bold text-[#1A1A1A] text-sm mb-3">{t('stageProgress.postUpdate')}</h4>
            <div className="flex gap-2 mb-3">
              {(['Progress', 'Issue', 'Complete'] as const).map((tag) => (
                <button
                  key={tag}
                  onClick={() => setSelectedTag(tag)}
                  className={`px-3 py-1.5 rounded-full text-xs font-semibold cursor-pointer whitespace-nowrap transition-colors ${
                    selectedTag === tag ? 'bg-[#E8621A] text-white' : 'bg-gray-100 text-[#6B7280]'
                  }`}
                >
                  {tag}
                </button>
              ))}
            </div>
            <textarea
              value={updateText}
              onChange={(e) => setUpdateText(e.target.value)}
              placeholder={t('stageProgress.sitePlaceholder')}
              rows={3}
              className="w-full px-3 py-2.5 rounded-xl border border-gray-200 text-sm text-[#374151] focus:outline-none focus:border-[#E8621A] transition-colors resize-none"
            />
            <div className="flex gap-2 mt-3">
              <button className="flex items-center gap-1.5 px-3 py-2 rounded-lg border border-gray-200 text-[#6B7280] text-sm cursor-pointer whitespace-nowrap">
                <i className="ri-image-add-line"></i>
                {t('stageProgress.addPhotos')}
              </button>
              <button
                onClick={() => { setShowUpdateForm(false); setUpdateText(''); }}
                className="flex-1 py-2 rounded-lg bg-[#E8621A] text-white text-sm font-semibold cursor-pointer whitespace-normal sm:whitespace-nowrap"
              >
                {t('stageProgress.postUpdateBtn')}
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Sticky bottom action */}
      {!showUpdateForm && (
        <div className="fixed bottom-16 left-0 right-0 px-4 pb-2 z-20">
          <button
            onClick={() => setShowUpdateForm(true)}
            className="w-full bg-[#E8621A] text-white py-4 rounded-2xl font-bold text-sm flex items-center justify-center gap-2 cursor-pointer shadow-lg whitespace-normal sm:whitespace-nowrap"
          >
            <i className="ri-add-circle-line text-lg"></i>
            {t('stageProgress.postUpdate')}
          </button>
        </div>
      )}
    </MgmtLayout>
  );
}
